import { zafClient } from "../Zendesk";

// Interfaces para o autocomplete do Zendesk
interface ZendeskAutocompleteRecord {
  id: string | number;
  name: string;
  custom_object_key: string;
  custom_object_fields: Record<string, unknown>;
  attributes?: Record<string, unknown>;
}

interface ZendeskAutocompleteResponse {
  records?: ZendeskAutocompleteRecord[];
  custom_object_records?: ZendeskAutocompleteRecord[];
  data?: ZendeskAutocompleteRecord[];
}

export interface ProductAutocompleteResult {
  id: string | number;
  label: string;
  subLabel?: string;
  codigo?: string;
  raw: ZendeskAutocompleteRecord;
}

// Configuração baseada nos seus field IDs reais
const CUSTOM_OBJECT_KEY = "produto"; // Ajuste se necessário
const TICKET_FIELD_ID = 38546069276180; // Campo "Produto (objeto)" dos seus constants

/**
 * Busca produtos usando o mesmo endpoint de autocomplete que o Zendesk usa
 */
export async function searchProductsAutocomplete(term: string): Promise<ProductAutocompleteResult[]> {
  const q = term.trim();
  if (q.length < 2) return [];

  try {
    console.log("🔍 Buscando produtos via autocomplete:", q);

    // Primeiro, tenta o endpoint de autocomplete padrão
    let response: ZendeskAutocompleteResponse;
    let url = `/api/v2/custom_objects/${CUSTOM_OBJECT_KEY}/records/autocomplete.json`;
    
    try {
      // Método 1: autocomplete com parâmetro name
      const params = new URLSearchParams({ name: q });
      response = await zafClient.request({
        url: `${url}?${params.toString()}`,
        type: "GET",
      }) as ZendeskAutocompleteResponse;
      
      console.log("✅ Autocomplete response (method 1):", response);
    } catch (error) {
      console.log("⚠️ Method 1 failed, trying method 2...", error);
      
      try {
        // Método 2: autocomplete com parâmetro query
        const params = new URLSearchParams({ query: q });
        response = await zafClient.request({
          url: `${url}?${params.toString()}`,
          type: "GET",
        }) as ZendeskAutocompleteResponse;
        
        console.log("✅ Autocomplete response (method 2):", response);
      } catch (error) {
        console.log("⚠️ Method 2 failed, trying method 3...", error);
        
        // Método 3: search endpoint
        url = `/api/v2/custom_objects/${CUSTOM_OBJECT_KEY}/records/search.json`;
        const params = new URLSearchParams({ query: q });
        response = await zafClient.request({
          url: `${url}?${params.toString()}`,
          type: "GET",
        }) as ZendeskAutocompleteResponse;
        
        console.log("✅ Search response (method 3):", response);
      }
    }

    // Normaliza a resposta independente da estrutura
    const records = response.records || response.custom_object_records || response.data || [];
    
    console.log("📋 Records encontrados:", records.length);

    return records.map((record: ZendeskAutocompleteRecord) => {
      const fields = record.custom_object_fields || record.attributes || {};
      
      const result: ProductAutocompleteResult = {
        id: record.id,
        label: record.name || String(fields.produto_comercial || fields.nome || "(sem nome)"),
        subLabel: String(fields.codigo_do_produto || fields.codigo || ""),
        codigo: String(fields.codigo_do_produto || fields.codigo || ""),
        raw: record
      };
      
      // Log para debug do produto específico
      if (result.label.includes("TV PTV32E20DSGWA") || (result.codigo && result.codigo.includes("99323048"))) {
        console.log("🎯 Produto alvo encontrado via autocomplete:", result);
      }
      
      return result;
    });

  } catch (error) {
    console.error("❌ Erro no autocomplete de produtos:", error);
    
    // Fallback: usar a busca manual anterior se o autocomplete falhar
    console.log("🔄 Usando fallback para busca manual...");
    return await fallbackProductSearch(term);
  }
}

/**
 * Define o produto selecionado no campo do ticket
 */
export async function setSelectedProductInTicket(recordId: string | number): Promise<void> {
  try {
    console.log("💾 Salvando produto no ticket:", recordId);
    
    await zafClient.set(`ticket.customField:${TICKET_FIELD_ID}`, recordId);
    
    console.log("✅ Produto salvo com sucesso no campo do ticket");
  } catch (error) {
    console.error("❌ Erro ao salvar produto no ticket:", error);
    throw error;
  }
}

/**
 * Busca manual como fallback (sua implementação atual)
 */
async function fallbackProductSearch(term: string): Promise<ProductAutocompleteResult[]> {
  console.log("🔄 Executando fallback search...");
  
  try {
    // Usa sua implementação atual como fallback
    const url = `/api/v2/custom_objects/${CUSTOM_OBJECT_KEY}/records`;
    const response = await zafClient.request({
      url,
      type: "GET",
    }) as { custom_object_records?: ZendeskAutocompleteRecord[] };

    const allRecords = response?.custom_object_records ?? [];
    
    const filtered = allRecords.filter((record) => {
      const fields = record.custom_object_fields || {};
      const searchText = [
        record.name,
        fields.produto_comercial,
        fields.codigo_do_produto,
        fields.codigo,
        fields.nome,
        fields.marca,
        fields.modelo
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      
      return searchText.includes(term.toLowerCase());
    });

    return filtered.map(record => {
      const fields = record.custom_object_fields || {};
      return {
        id: record.id,
        label: record.name || String(fields.produto_comercial || "(sem nome)"),
        subLabel: String(fields.codigo_do_produto || ""),
        codigo: String(fields.codigo_do_produto || ""),
        raw: record
      };
    });
    
  } catch (error) {
    console.error("❌ Erro no fallback search:", error);
    return [];
  }
}
